import { Link, type LucideIcon } from 'lucide-react';

import { cn } from '@/lib/utils';
import { Button, ButtonProps } from '@/components/ui/button';
import { Card, CardContent, CardFooter, CardHeader } from '@/components/ui/card';
import { Avatar as ApplicationAvatar } from 'src/widgets/AppIdentityCard/ui/Avatar';
import { AppRequestStatusEnum } from 'src/graphql/schema';
import { Badge } from '@/components/ui/badge';

import type { VendorRequestsFilter, VendorRequest } from '../model';

type Direction = VendorRequestsFilter;

type Props = {
  request: VendorRequest;
  direction: Direction;
  onAction: (type: 'decline' | 'link', requestId: string) => void;
};

type HeaderProps = {
  request: VendorRequest;
  direction: Direction;
};

type BadgeInfo = { label: string; className: string };

const getBadgeInfo = (status: AppRequestStatusEnum, direction: Direction, vendorName: string): BadgeInfo | null => {
  if (status === AppRequestStatusEnum.Declined) return { label: 'Declined', className: 'bg-red-100 text-destructive' };
  if (status === AppRequestStatusEnum.Accepted) return { label: 'Accepted', className: 'bg-green-100 text-green-800' };
  if (status === AppRequestStatusEnum.Pending && direction === 'received')
    return { label: 'Waiting on your response', className: 'bg-amber-100 text-amber-800' };
  if (status === AppRequestStatusEnum.Pending && direction === 'sent')
    return { label: `Waiting on ${vendorName}`, className: 'bg-muted text-slate-700' };

  return null;
};

export const Header = ({ request, direction }: HeaderProps) => {
  const contactLine = `${request.contactName} · ${request.contactEmail}`;
  const badge = getBadgeInfo(request.status, direction, request.vendorName);

  return (
    <CardHeader className="flex-row items-start justify-between gap-4 px-5 pt-4">
      <div className="flex items-start gap-3">
        <ApplicationAvatar
          appId={request.id}
          name={request.vendorName}
          logoUrl={null}
          claimed
          ownership="vendor"
          size="sm"
        />
        <div className="space-y-0.5">
          <div className="font-semibold text-sm">{request.vendorName}</div>
          <div className="text-xs text-muted-foreground">{contactLine}</div>
        </div>
      </div>
      {Boolean(badge) && (
        <Badge
          data-testid="request-card-badge"
          variant="pill"
          className={cn('border-0 whitespace-nowrap font-semibold', badge?.className)}
        >
          {badge?.label}
        </Badge>
      )}
    </CardHeader>
  );
};

const getInfoItems = (request: VendorRequest, direction: Direction): IOptionType[] => {
  if (direction === 'received') {
    const items = [{ label: 'Organization name', value: request.vendorName }];

    if (request.requesterCompanyWebsite) items.push({ label: 'Website', value: request.requesterCompanyWebsite });
    if (request.appName) items.push({ label: 'Suggested application', value: request.appName });

    return items;
  }

  if (direction === 'sent') {
    const items = [
      { label: 'Sent by', value: request.requesterCompanyName },
      { label: 'Contact name', value: request.contactName },
      { label: 'Sent to', value: request.contactEmail },
    ];

    if (request.appName) items.push({ label: 'Suggested application', value: request.appName });

    return items;
  }

  return [
    { label: 'Sent by', value: request.requesterCompanyName },
    { label: 'Sent to', value: request.contactEmail },
  ];
};

type InfoItemsProps = {
  request: VendorRequest;
  direction: Direction;
};

export const InfoItems = ({ request, direction }: InfoItemsProps) => {
  return (
    <div className="rounded-lg border border-border p-4 space-y-2">
      {getInfoItems(request, direction).map((item) => (
        <div key={item.label} className="flex items-center justify-between gap-3 text-sm">
          <span data-testid="request-card-info-label" className="text-muted-foreground">
            {item.label}
          </span>
          <span data-testid="request-card-info-value" className="font-semibold text-card-foreground">
            {item.value}
          </span>
        </div>
      ))}
    </div>
  );
};

type ReasonProps = {
  reason?: string | null;
};

export const Reason = ({ reason }: ReasonProps) => {
  if (!reason) return null;

  return (
    <div className="text-sm">
      <span className="text-muted-foreground">Reason:</span> <span className="italic">&quot;{reason}&quot;</span>
    </div>
  );
};

type ActionType = 'decline' | 'link';

type ActionsProps = {
  actions: { type: ActionType; label: string }[];
  requestId: string;
  onAction: (type: ActionType, requestId: string) => void;
};

const BUTTON_STYLE_BY_ACTION: Record<ActionType, { variant: ButtonProps['variant']; icon?: LucideIcon }> = {
  decline: { variant: 'destructive' },
  link: { variant: 'default', icon: Link },
};

export const Actions = ({ actions, requestId, onAction }: ActionsProps) => {
  if (!actions.length) return null;

  return (
    <CardFooter className="justify-end gap-2 px-5 pb-4">
      {actions.map((action) => {
        const { variant, icon: Icon } = BUTTON_STYLE_BY_ACTION[action.type];

        return (
          <Button key={action.type} variant={variant} onClick={() => onAction(action.type, requestId)}>
            {Icon && <Icon className="size-4" />}
            {action.label}
          </Button>
        );
      })}
    </CardFooter>
  );
};

const getActions = (policy: VendorRequest['policy']): ActionsProps['actions'] => {
  const actions: ActionsProps['actions'] = [];

  if (policy.decline) actions.push({ type: 'decline', label: 'Decline' });
  if (policy.accept) actions.push({ type: 'link', label: 'Link' });

  return actions;
};

export const RequestCard = ({ request, direction, onAction }: Props) => {
  return (
    <Card>
      <Header request={request} direction={direction} />
      <CardContent className="space-y-4 px-5">
        <div className="space-y-2">
          <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground block">
            What needs protecting?
          </span>
          <p className="text-sm text-card-foreground border-l-2 border-border pl-3">{request.description}</p>
        </div>
        <InfoItems request={request} direction={direction} />
        <Reason reason={request.declineReason} />
      </CardContent>
      <Actions actions={getActions(request.policy)} requestId={request.id} onAction={onAction} />
    </Card>
  );
};
